import { StyleSheet, Document, Page, Text, View, Image as PdfImage, Link } from '@react-pdf/renderer'
import React from 'react'
import HeaderOfPDF from './Header'
import FooterOfPDF from './Footer'
import { OpForPDFType } from '../Modals/ViewPCP/GeneratePDFModal'
import MainFieldsOfPDF from './MainFields'
import { stageDisplayNumber } from '../../pages/ClientPanel/ProductionFlow/stageNumbering'
import { stripHtml } from '../../utils/stripHtml'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 112,
    paddingBottom: 65,
    paddingHorizontal: 30,
  },
  pageNumber: {
    position: 'absolute',
    top: 32,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 9,
    color: '#000',
  },
  pageTitle: {
    position: 'absolute',
    top: 68,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000',
  },
  section: {
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '1px solid #000',
  },
  servicesContainer: {
    marginBottom: 10,
  },
  divider: {
    marginVertical: 8,
    borderBottom: '1px solid #000',
  },
  paragraph: {
    marginBottom: 10,
    fontSize: 10,
    lineHeight: 1.3,
    color: '#000',
  },
  materialItem: {
    marginBottom: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  serviceItem: {
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 1,
    fontSize: 9,
    color: '#000',
    textTransform: 'uppercase',
  },
  value: {
    marginBottom: 3,
    fontSize: 10,
    color: '#000',
    lineHeight: 1.25,
  },
  serviceNumber: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  emptyMessage: {
    fontSize: 10,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  qrCodeContainer: {
    position: 'absolute',
    bottom: 100,
    right: 40,
    width: 80,
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
})

interface BodyOfPDFType {
  data?: OpForPDFType | null
  qrCodeDataUrl?: string
}

const formatDate = (dateStr?: string | Date | null): string => {
  if (!dateStr) return '-'
  try {
    const str = typeof dateStr === 'string' ? dateStr : dateStr.toISOString()
    const dateOnly = str.split('T')[0]
    const [year, month, day] = dateOnly.split('-')
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    }
    return str
  } catch (e) {
    return '-'
  }
}

const BodyOfPDF: React.FC<BodyOfPDFType> = ({ data, qrCodeDataUrl }) => {
  const productCoverImage = data?.OrderItem?.Attachments?.[0]?.GeneralFile?.fileUrl || data?.Script?.Product?.Attachments?.[0]?.GeneralFile?.fileUrl

  const pushFields = (): { label: string, subLabel?: string, value: string }[] => {
    return [
      ...(data?.OrderItem.length ? [{
        label: 'Comprimento',
        value: `${data.OrderItem.length}mm`
      }] : []),
      ...(data?.OrderItem.diameter ? [{
        label: 'Diâmetro',
        value: `${data.OrderItem.diameter}mm`
      }] : []),
      ...(data?.OrderItem.thickness ? [{
        label: 'Espessura',
        value: `${data.OrderItem.thickness}mm`
      }] : []),
      ...(data?.OrderItem.width ? [{
        label: 'Largura',
        value: `${data.OrderItem.width}mm`
      }] : []),
      ...(data?.OrderItem.weight ? [{
        label: 'Peso',
        value: `${data.OrderItem.weight}kg`
      }] : [])
    ]
  }

  const getItemName = (item: any): string => {
    return item.Stock?.Code.code || item.Stock?.description || item.Resource?.name || item.Resource?.description || 'Item não identificado'
  }

  return (
    <Document>
      {productCoverImage && (
        <Page size="A4" style={styles.page}>
          <HeaderOfPDF />
          <Text fixed style={styles.pageTitle}>{`${data?.codeOfOP || ''} - Capa`}</Text>
          <View style={[styles.serviceItem, { flex: 1, marginHorizontal: 20, marginBottom: 20 }]}>
            <Text style={styles.label}>Produto:</Text>
            <Text style={styles.value}>
              {stripHtml(data?.OrderItem?.ploomesProductName) || data?.Script?.Product?.name || 'Anexo do Produto'}
            </Text>
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
              <PdfImage source={{ uri: productCoverImage }} style={{ objectFit: 'contain', width: '100%', maxHeight: 550 }} />
            </View>
          </View>
          <FooterOfPDF qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={data?.id?.toString()} />
        </Page>
      )}
      <Page size="A4" style={styles.page}>
        <HeaderOfPDF version={data?.version} />
        <Text
          fixed
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        />
        <Text fixed style={styles.pageTitle}>{`${data?.codeOfOP || ''}`}</Text>
        <MainFieldsOfPDF infoFields={[{
          label: 'Produto',
          subLabel: 'Item da venda',
          value: stripHtml(data?.OrderItem.ploomesProductName) || '',
        }, ...(data?.Script ? [{
          label: 'Produto do roteiro',
          value: data.Script.Product?.name || '',
        }] : []), ...pushFields(), ...(data?.OrderItem?.Order ? [{
          label: 'Previsão de faturamento',
          value: data.OrderItem.Order.billingDate ? formatDate(data.OrderItem.Order.billingDate) : '-',
        }] : [])]} />
        {data?.StockInProduction && data.StockInProduction.length > 0 ? (
          <View style={styles.section}>
            {data.StockInProduction.map((stock, index) => (
              <View key={index} wrap={false} minPresenceAhead={50} style={styles.materialItem}>
                <Text style={styles.label}>{stock.stockId ? 'Material' : 'Insumo'}:</Text>
                <Text style={styles.value}>{getItemName(stock)}</Text>
                <Text style={styles.label}>Quantidade:</Text>
                <Text style={styles.value}>{stock.usedQuantity} {stock.usedUnitOfMeasurement.toLowerCase()}</Text>
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.divider} />
        )}

        <View style={styles.servicesContainer}>
          {data?.Service_Stage?.length ? (
            data.Service_Stage.map((root, parentIndex) => {
              const children = (root.Children ?? []).slice().sort(
                (a, b) => (a.index ?? 0) - (b.index ?? 0),
              )
              const renderRow = (
                stageInfo: any,
                label: string,
                name: string,
                description: string,
                observation?: string,
              ) => {
                const attachmentUrl = stageInfo.FileInServiceStage?.[0]?.GeneralFile?.fileUrl;
                const attachmentTitle = stageInfo.FileInServiceStage?.[0]?.GeneralFile?.title;

                return (
                  <View key={stageInfo.id} wrap={false} minPresenceAhead={100} style={styles.serviceItem}>
                    <Text style={styles.label}>Serviço:</Text>
                    <Text style={styles.value}>
                      <Text style={styles.serviceNumber}>{label} - </Text>
                      {name}
                    </Text>
                    {attachmentUrl ? (
                      <View>
                        <Text style={styles.label}>Anexo:</Text>
                        <Link src={attachmentUrl} style={{ ...styles.value, color: 'blue', textDecoration: 'underline' }}>
                          {attachmentTitle || attachmentUrl}
                        </Link>
                      </View>
                    ) : null}
                    <Text style={styles.label}>Descrição:</Text>
                    <Text style={styles.value}>{description}</Text>
                    {observation ? (
                      <View>
                        <Text style={styles.label}>Observações:</Text>
                        <Text style={styles.value}>{observation}</Text>
                      </View>
                    ) : null}
                  </View>
                );
              }
              return (
                <React.Fragment key={root.id}>
                  {renderRow(
                    root,
                    stageDisplayNumber(parentIndex),
                    root.Stage.name,
                    root.description,
                    root.observation,
                  )}
                  {children.map((child, childIndex) =>
                    renderRow(
                      child,
                      stageDisplayNumber(parentIndex, childIndex),
                      child.Stage.name,
                      child.description,
                      child.observation,
                    ),
                  )}
                </React.Fragment>
              )
            })
          ) : (
            <Text style={styles.emptyMessage}>Nenhum serviço especificado.</Text>
          )}
        </View>

        <FooterOfPDF qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={data?.id?.toString()} />
      </Page>
    </Document>
  )
}

export default BodyOfPDF